/**
 * Scheduled order store
 *
 * Defers a buy/sell to a chosen moment instead of sending it immediately. The
 * order itself is unchanged — side, quantity and OCO TP/SL all come from the
 * order panel via `useTradingLogic`, so there is exactly one order-building
 * path and scheduling only decides WHEN it runs.
 *
 * Modes:
 *   now            send immediately (scheduling off)
 *   candle-close   fire as the current bar closes
 *   before-close   fire N seconds before the bar closes (default 1s)
 *   datetime       fire at a wall-clock time
 *
 * A pending order survives a re-render but deliberately NOT a reload: a queued
 * live order that fires after you've closed the tab is not something anyone
 * should have to reason about.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export type ScheduleMode = 'now' | 'candle-close' | 'before-close' | 'datetime';

export interface PendingOrder {
  action: 'Buy' | 'Sell';
  /** When it fires, ms epoch. */
  fireAt: number;
  /** Human label for the chip, e.g. "1s before close". */
  label: string;
}

interface ScheduledOrderState {
  mode: ScheduleMode;
  /** Lead time for 'before-close', in seconds. */
  leadSeconds: number;
  /** Target wall-clock time for 'datetime', as an ISO local string. */
  atTime: string;
  pending: PendingOrder | null;

  setMode: (mode: ScheduleMode) => void;
  setLeadSeconds: (s: number) => void;
  setAtTime: (t: string) => void;
  setPending: (p: PendingOrder | null) => void;
  cancel: () => void;
}

export const useScheduledOrderStore = create<ScheduledOrderState>((set) => ({
  mode: 'now',
  leadSeconds: 1,
  atTime: '',
  pending: null,

  setMode: (mode) => set({ mode }),
  setLeadSeconds: (leadSeconds) => set({ leadSeconds: Math.max(0, leadSeconds) }),
  setAtTime: (atTime) => set({ atTime }),
  setPending: (pending) => set({ pending }),
  cancel: () => set({ pending: null }),
}));

/**
 * When the bar containing `now` closes, in ms epoch.
 *
 * Bars are aligned to the epoch on a fixed interval, which is how the chart
 * builds them, so this needs no reference to the candle series.
 */
export function currentCandleCloseMs(nowMs: number, intervalMinutes: number): number {
  const intervalMs = Math.max(intervalMinutes, 1) * 60_000;
  return Math.floor(nowMs / intervalMs) * intervalMs + intervalMs;
}

/**
 * Resolve a mode into a fire time and a label, or null when it can't be
 * scheduled (a datetime in the past, say — better to refuse than to fire
 * instantly and surprise someone).
 */
export function resolveFireTime(
  mode: ScheduleMode,
  nowMs: number,
  intervalMinutes: number,
  leadSeconds: number,
  atTime: string
): { fireAt: number; label: string } | null {
  if (mode === 'now') return { fireAt: nowMs, label: 'now' };

  if (mode === 'candle-close') {
    return {
      fireAt: currentCandleCloseMs(nowMs, intervalMinutes),
      label: 'at candle close',
    };
  }

  if (mode === 'before-close') {
    const close = currentCandleCloseMs(nowMs, intervalMinutes);
    const fireAt = close - leadSeconds * 1000;
    // Already past that point in this bar — roll to the next one rather than
    // firing immediately, which is never what "before close" meant.
    const rolled = fireAt <= nowMs ? fireAt + intervalMinutes * 60_000 : fireAt;
    return { fireAt: rolled, label: `${leadSeconds}s before close` };
  }

  const t = Date.parse(atTime);
  if (!Number.isFinite(t) || t <= nowMs) return null;
  return {
    fireAt: t,
    label: new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
}

export const useScheduleMode = (): ScheduleMode => useScheduledOrderStore((s) => s.mode);
export const usePendingOrder = (): PendingOrder | null =>
  useScheduledOrderStore(useShallow((s) => s.pending));
